import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { enqueueSnackbar } from '~/utils/notifications';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { rollbackCreatedRois } from '~/hooks/structure-set/rollbackCreatedRois';
import { useGetOrCreateDestination } from '~/hooks/structure-set/useGetOrCreateDestination';
import { CouchModelId, CouchVariant } from '~/domain/couchtop/couchLibrary';
import { placeCouchModel } from '~/domain/couchtop/placeCouchModel';
import { DicomColor } from '~/domain/dicom/dicomColor';
import {
    RoiGenerationAlgorithmType,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { STRUCTURE_CODES } from '~/domain/dicom/structureCodes';
import { Image3D } from '~/domain/patient/image3d';
import { writeRoiContours } from '~/domain/structures/writeRoiContours';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';

/** Row edit for one component of the selected variant, aligned 1:1(by
 *  index) with `variant.components` — the panel's own row order.
 *  Readonly like the couch library itself: apply INPUT, never mutated. */
export interface CouchModelApplyRow {
    readonly name: string;
    readonly color: DicomColor;
}

export interface CouchModelApplyParams {
    readonly image: Image3D;
    /** The couch library MODEL id (e.g. `varian_exact_igrt`) — distinct
     *  from `variant.id` (e.g. `thin`), both recorded in the provenance. */
    readonly couchModelId: CouchModelId;
    readonly variant: CouchVariant;
    /** The couch top's continuous voxel Y — the SAME value the panel
     *  readout and the viewport line render from, handed through
     *  unconverted so placement resolves it in voxel space itself
     *  (see `placeCouchModel`). */
    readonly couchTopVoxelY: number;
    /** One entry per `variant.components`, same order. */
    readonly rows: readonly CouchModelApplyRow[];
}

export interface UseCouchModelApplicationOptions {
    environment: Environment | null;
    onSuccess: () => void;
    updateView: () => void;
}

/**
 * Compact, deterministic provenance record for `generation.description`:
 * the library entry the geometry came from. Order is fixed so two runs
 * with the same inputs produce a byte-identical record.
 * `roiGenerationDescription`'s own setter truncates to the DICOM LO budget
 * (64 chars); every reachable id here is short enough that truncation is
 * not a practical concern.
 */
function buildGenerationDescription(
    couchModelId: string,
    variantId: string
): string {
    return `couch ${couchModelId}/${variantId}`;
}

/**
 * Places a library couch variant and writes it into the structure set as
 * real SUPPORT ROIs — the Apply path for the couch-model panel.
 *
 * Every destination is resolved (created) BEFORE any geometry is written:
 * a name collision or creation failure on a LATER component must not
 * leave an EARLIER one holding real couch geometry a TPS would compute
 * dose through. Geometry + density for every already-resolved component
 * are then written inside ONE `tracker.command`, so the undo stack and
 * any changeset listener see the whole application as a single event.
 *
 * `useGetOrCreateDestination`'s creation path (`useStructureSet`'s
 * `createNewRoi`) commits its own new ROI immediately, in a
 * `tracker.command` of its own — that side effect is not ours to avoid.
 * A failure anywhere after the first creation therefore
 * rolls back every ROI this call created, via the same tracked-deletion
 * event `useBodyContourApplication` uses for its single-destination
 * case, so "neither ROI committed" holds for the geometry AND for the
 * stub structures that would otherwise be left behind, stamped AUTOMATIC,
 * with nothing the user asked for.
 */
export function useCouchModelApplication({
    environment,
    onSuccess,
    updateView,
}: UseCouchModelApplicationOptions) {
    // Not a component, but every message this hook raises is read by a
    // clinician, so they come from the catalogs like any other. `useIntl`
    // is legal in any hook rendered under the app's IntlProvider.
    const intl = useIntl();
    const session = useSessionOrNull();
    const [isApplying, setIsApplying] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
    });

    const apply = useCallback(
        async (params: CouchModelApplyParams) => {
            if (isApplying) {
                enqueueSnackbar(
                    intl.formatMessage({ id: 'couch.error.applyInProgress' }),
                    { variant: 'warning' }
                );
                return;
            }

            const structureSet = environment?.structureSet;
            if (!structureSet || !session) {
                // A dose-writing button that does nothing at all is the
                // worst available failure mode: the user cannot tell a
                // refusal from a success. Every other path in this
                // function surfaces something, so this one does too.
                enqueueSnackbar(
                    intl.formatMessage({ id: 'couch.error.notReady' }),
                    { variant: 'error' }
                );
                return;
            }

            setIsApplying(true);
            // ROI numbers THIS call created, in creation order — rolled
            // back on any failure, cleared once the whole apply commits.
            const createdRoiNumbers: number[] = [];

            // Missing-UID logging, list draining and per-ROI failure
            // handling all live in the shared helper — see its own doc.
            const rollbackCreated = () =>
                rollbackCreatedRois(
                    structureSet.sopInstanceUID,
                    createdRoiNumbers
                );

            try {
                if (params.rows.length !== params.variant.components.length) {
                    // Caller (the panel) invariant — every component gets
                    // a row edit. Never reachable from the UI; a loud
                    // failure here beats silently dropping a component's
                    // geometry. INSIDE the try on purpose: the panel
                    // calls this as `void apply(...)`, so a throw from
                    // outside would surface as an unhandled rejection —
                    // invisible, which is the opposite of loud.
                    throw new Error(
                        'useCouchModelApplication: rows must align 1:1 with the variant’s components'
                    );
                }

                // The panel disables Apply on a part-volume; this is the
                // read-side refusal SR-WOR-H-37-3 asks for. Placement
                // never reads a pixel, so nothing below would notice that
                // the slices it extrudes over have no data yet, and
                // `writeRoiContours` resolves their SOP UIDs happily.
                // Inside the try for the same reason as the invariant
                // above, and with counts only: no identifier of the series
                // may reach the snackbar.
                if (!params.image.isComplete) {
                    throw new Error(
                        'useCouchModelApplication: image is not complete ' +
                            `(${params.image.loadedSliceCount} of ` +
                            `${params.image.expectedSliceCount} slices); ` +
                            'refusing to extrude a couch over slices with no data'
                    );
                }

                // Fails fast on a grid this feature cannot place a couch
                // on, BEFORE any destination exists — see
                // placeCouchModel's own doc.
                // Grouped per component by placeCouchModel itself, index-
                // aligned with `variant.components` (and so with
                // `params.rows`) — no name-keyed re-join here.
                const placed = placeCouchModel(
                    params.variant,
                    params.couchTopVoxelY,
                    params.image.grid
                );

                const description = buildGenerationDescription(
                    params.couchModelId,
                    params.variant.id
                );

                // 1. Resolve EVERY destination before writing any.
                const destinations: RegionOfInterest[] = [];
                for (let i = 0; i < params.variant.components.length; i++) {
                    const row = params.rows[i];
                    const dest = await getOrCreateDestination(null, row.name, {
                        interpretedType: RoiInterpretedType.SUPPORT,
                        generation: {
                            algorithm: RoiGenerationAlgorithmType.AUTOMATIC,
                            description,
                        },
                        identificationCode: STRUCTURE_CODES.couchSupport,
                        color: row.color,
                    });
                    if (!dest) {
                        // getOrCreateDestination already surfaced its own
                        // failure snackbar (name collision, refused
                        // creation, ...).
                        await rollbackCreated();
                        return;
                    }
                    createdRoiNumbers.push(dest.roiNumber);
                    destinations.push(dest);
                }

                // 2. Geometry + density for every component, in ONE
                // tracked command, in library order.
                session.context.tracker.command(() => {
                    for (let i = 0; i < placed.length; i++) {
                        const { component, contours } = placed[i];
                        const dest = destinations[i];
                        const domainRoi = structureSet.parent.rois.find(
                            (r) => r.roiNumber === dest.roiNumber
                        );
                        if (!domainRoi) {
                            // Defensive: getOrCreateDestination just
                            // resolved this ROI in the same structure
                            // set, so it must be present.
                            throw new Error(
                                `useCouchModelApplication: destination ROI ` +
                                    `${dest.roiNumber} not found in the ` +
                                    `domain structure set`
                            );
                        }
                        writeRoiContours(
                            structureSet.parent.dicomStructureSet,
                            domainRoi.dicomRoi,
                            params.image,
                            contours
                        );
                        // Written straight to DICOM, outside the contour
                        // history.
                        domainRoi.markContourEdited(null);
                        domainRoi.setElectronDensityOverride(
                            component.density.relElectron
                        );
                        domainRoi.setMassDensityOverride(
                            component.density.relMass
                        );
                    }
                });

                // 3. Resync each engine ROI from the geometry just
                // written. The engine RegionOfInterest was constructed
                // when the destination's OWN creation command flushed —
                // BEFORE the write above — and snapshots the (then empty)
                // domain contours once; nothing refreshes it from a later
                // `DicomRoi.contours` write, so without this the exported
                // DICOM carries the couch while the viewport (and volume/
                // emptiness readouts) show nothing — geometry the user
                // cannot verify. After the command on purpose: a write
                // failure above rolls the ROIs back instead.
                for (const dest of destinations) {
                    dest.reloadContoursFromDicom();
                }

                // Fully committed — nothing left to roll back.
                createdRoiNumbers.length = 0;
                updateView();
                enqueueSnackbar(intl.formatMessage({ id: 'couch.applied' }), {
                    variant: 'success',
                });
                onSuccess();
            } catch (e) {
                await rollbackCreated();
                console.error('Couch model apply error:', e);
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'couch.error.applyFailed' },
                        { error: getErrorMessage(e) }
                    ),
                    { variant: 'error' }
                );
            } finally {
                setIsApplying(false);
            }
        },
        [
            environment,
            session,
            isApplying,
            getOrCreateDestination,
            updateView,
            onSuccess,
            intl,
        ]
    );

    return { isApplying, apply };
}
